import { action, computed, makeObservable, observable, override } from "mobx"
import { formatUnits, parseUnits } from "viem"

import { LcAddress } from "@/domains/core"

import type { TokenApiDAO, TokenDiscoverer, TokenSource } from "./types"

export class TokenStore {
  // hard coded values (for now.. extend API soon)
  chainId: number
  chainSlug: string

  // Static data (from API)
  address: LcAddress
  symbol: string
  name: string
  decimals: number
  isWhitelisted: boolean
  isSwapToken: boolean
  isIncentiveToken: boolean

  // Tag-derived boolean fields (from API, Classic Pools, or other sources)
  hasBlueChipTag: boolean = false
  hasNewTag: boolean = false
  hasJunkTag: boolean = false
  hasRuggedTag: boolean = false

  // Dynamic data (prices - always numbers, never null)
  apiPrice: number = 0
  onChainPrice: number = 0
  debankPrice: number = 0

  // Balance data
  balance: bigint = 0n
  source: TokenSource

  // Discovery tracking - which automated process found this token first (immutable)
  readonly discoveredBy: TokenDiscoverer

  constructor(data: TokenApiDAO, source: TokenSource = "system", discoveredBy: TokenDiscoverer = "token-api") {
    // Hard coded to base chain
    this.chainId = 8453
    this.chainSlug = "base"

    // Static data
    this.address = data.address
    this.symbol = data.symbol
    this.name = data.name
    this.decimals = data.decimals
    this.isWhitelisted = data.isWhitelisted
    this.isSwapToken = data.isSwapToken
    this.isIncentiveToken = data.isIncentiveToken

    // Initialize tag-derived fields from API data
    this.updateTagDerivedFields(data.tags)

    // Initialize API price from the API data
    this.apiPrice = parseFloat(data.priceUsd) || 0

    // User data
    this.source = source

    // Discovery tracking - immutable, set once at creation
    this.discoveredBy = discoveredBy

    makeObservable(this, {
      // Observable properties
      // address: observable, reactivity not required
      // symbol: observable, reactivity not required
      // name: observable,reactivity not required
      // decimals: observable,reactivity not required
      isWhitelisted: observable,
      isSwapToken: observable,
      isIncentiveToken: observable,

      apiPrice: observable,
      onChainPrice: observable,
      debankPrice: observable,
      balance: observable,
      // source: observable,reactivity not required
      // discoveredBy: observable, reactivity not required (immutable)

      // Extracted tags
      hasBlueChipTag: observable,
      hasNewTag: observable,
      hasJunkTag: observable,
      hasRuggedTag: observable,

      // Computed getters
      bestPrice: computed,
      displayBalance: computed,
      tokenId: computed,
      // imageURL: computed, doesn't need to be observable
      // scanTokenURL: computed, reactivity not required
      balanceValueUSD: computed,

      // Actions
      updateApiPrice: action,
      updateOnChainPrice: action,
      updateDebankPrice: action,
      updateBalance: action,
      updateStaticData: action,
    })
  }

  // Computed best price with explicit priority
  get bestPrice(): number {
    if (this.onChainPrice > 0) return this.onChainPrice
    if (this.debankPrice > 0) return this.debankPrice
    if (this.apiPrice > 0) return this.apiPrice
    return 0
  }

  // Computed display balance
  get displayBalance(): string {
    return this.formatUnits(this.balance)
  }

  // Computed token ID: <address>-<chainId>
  // Used for multi-chain token identification in GraphQL queries and other contexts
  get tokenId(): string {
    return `${this.address.toString()}-${this.chainId}`
  }

  get imageURL(): string {
    return `https://cdn.jsdelivr.net/gh/unified-defi/equalizer-tokens@latest/base/tokens/128x128/${this.address}.png`
  }

  get scanTokenURL(): string {
    return `https://basescan.org/token/${this.address}`
  }

  get balanceValueUSD(): string {
    return (Number(this.displayBalance) * this.bestPrice).toFixed(4)
  }

  calculateValuePercentage(value: bigint, percentage: number): string {
    // Convert percentage to BigInt (multiply by 10000 to preserve 4 decimal places)
    const precision = 10000
    const percentageBigInt = BigInt(Math.floor(percentage * precision))

    // Calculate: (balance * percentage) / 10000
    const result = (value * percentageBigInt) / BigInt(precision)

    // format to this tokens decimals
    return this.formatUnits(result)
  }

  calculateValueUSD(amount: number): number {
    return amount * this.bestPrice
  }

  // Explicit update methods
  updateApiPrice(price: number) {
    if (price < 0) return
    this.apiPrice = price
  }

  updateOnChainPrice(price: number) {
    if (price < 0) return
    this.onChainPrice = price
  }

  updateDebankPrice(price: number) {
    if (price < 0) return
    this.debankPrice = price
  }

  updateBalance(balance: bigint) {
    if (balance < 0n) return
    this.balance = balance
  }

  /**
   * Token specific unit formatting (uses internal decimals)
   * @param wei
   * @returns
   */
  formatUnits(wei: bigint): string {
    return formatUnits(wei, this.decimals)
  }

  displayUnits(wei: bigint, decimalPlaces: number): string {
    const value = Number(formatUnits(wei, this.decimals))
    return value.toLocaleString("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    })
  }

  parseUnits(value: string): bigint {
    return parseUnits(value, this.decimals)
  }

  /**
   * Convert a bigint to a string with a fixed number of decimal places
   * @param value
   * @param decimalPlaces
   * @returns
   */
  toFixed(value: bigint, decimalPlaces = this.decimals): string {
    return this.formatUnits(value)
    // .toFixed(decimalPlaces)
  }

  // Update tag-derived fields from tag array
  private updateTagDerivedFields(tags: string[] | undefined) {
    if (!tags || !Array.isArray(tags)) return

    // Reset all tag fields first
    this.hasBlueChipTag = false
    this.hasNewTag = false
    this.hasJunkTag = false
    this.hasRuggedTag = false

    // Set specific tag fields based on array content
    tags.forEach((tag) => {
      const normalizedTag = tag.toLowerCase()
      switch (normalizedTag) {
        case "blue-chip":
          this.hasBlueChipTag = true
          break
        case "new":
          this.hasNewTag = true
          break
        case "junk":
          this.hasJunkTag = true
          break
        case "rugged":
          this.hasRuggedTag = true
          break
      }
    })
  }

  // Update static data (useful for refreshing from API)
  updateStaticData(data: Partial<TokenApiDAO>) {
    if (data.symbol !== undefined) this.symbol = data.symbol
    if (data.name !== undefined) this.name = data.name
    if (data.decimals !== undefined) this.decimals = data.decimals
    if (data.isWhitelisted !== undefined) this.isWhitelisted = data.isWhitelisted
    if (data.isSwapToken !== undefined) this.isSwapToken = data.isSwapToken
    if (data.isIncentiveToken !== undefined) this.isIncentiveToken = data.isIncentiveToken
    if (data.priceUsd !== undefined) this.apiPrice = parseFloat(data.priceUsd) || 0

    // Update tag-derived fields from API data
    if (data.tags) {
      this.updateTagDerivedFields(data.tags)
    }
  }
}

/**
 * Native token store that delegates price to its wrapped ERC20 token (e.g. ETH → WETH).
 * Ensures native token always shows the same USD price as the wrapped token without
 * requiring separate price sync or repository inclusion.
 */
export class NativeTokenStore extends TokenStore {
  constructor(
    data: TokenApiDAO,
    source: TokenSource,
    discoveredBy: TokenDiscoverer,
    private readonly wrappedToken: TokenStore,
  ) {
    super(data, source, discoveredBy)
    makeObservable(this, {
      bestPrice: override,
    })
  }

  override get bestPrice(): number {
    return this.wrappedToken.bestPrice
  }
}
